import { useEffect, useRef } from 'react';
import { useTabStore } from '../stores/tabStore';
import { useAgentTreeStore } from '../stores/agentTreeStore';
import type { TreeItem } from '../types';

/**
 * Hook that validates open tabs against loaded data.
 *
 * Open items are an agent's — each agent keeps its own, keyed by its id — so
 * each agent's are checked once, after that agent's tree and my conversations
 * with it have both answered: before, every tab would look stale. An agent
 * nobody has opened is not asked for just to check it; its items are checked
 * when it is opened. A routine is not a tab — it opens on the project's page —
 * so a strip stored while it was one loses that tab here.
 */
export function useTabValidation() {
  const tabsByProject = useTabStore((s) => s.tabsByProject);
  const trees = useAgentTreeStore((s) => s.trees);
  const conversations = useAgentTreeStore((s) => s.conversations);
  const treeLoad = useAgentTreeStore((s) => s.treeLoad);
  const conversationLoad = useAgentTreeStore((s) => s.conversationLoad);
  const validated = useRef(new Set<string>());

  useEffect(() => {
    for (const [agentId, state] of Object.entries(tabsByProject)) {
      if (validated.current.has(agentId)) continue;
      if (treeLoad[agentId] !== 'ready' || conversationLoad[agentId] !== 'ready') continue;
      validated.current.add(agentId);

      const validIds = new Set<string>();
      const collect = (items: TreeItem[]) => {
        for (const item of items) {
          validIds.add(item.id);
          if (item.children) collect(item.children);
        }
      };
      collect(trees[agentId] ?? []);
      collect(conversations[agentId] ?? []);

      const stale = state.tabs.filter((tab) => {
        // Don't close draft tabs or tabs with special IDs
        if (tab.isDraft || tab.itemId === 'draft-new') return false;
        // A deck or workbook an agent built is its work, never in the tree
        // (docs/work.md): its viewer says so if it is gone.
        if (tab.itemType === 'workbook' || tab.itemType === 'presentation') return false;
        return !validIds.has(tab.itemId);
      });
      if (stale.length > 0) {
        console.log('[TabValidation] Closing stale tabs:', stale.map((t) => t.itemId));
        for (const tab of stale) useTabStore.getState().closeTab(agentId, tab.id);
      }
    }
  }, [tabsByProject, trees, conversations, treeLoad, conversationLoad]);
}
